import { Button } from '@/components/ui/button'
import { GlassCard } from '@/components/ui/glass-card'
import { Progress } from '@/components/ui/progress'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useActiveStudyCount } from '@/hooks/useActiveStudyCount'
import { useUserLimits } from '@/hooks/useUserLimits'
import { useUtcMidnightCountdown } from '@/hooks/useUtcMidnightCountdown'
import type { UserLimits as UserLimitsType } from '@/types/auth'
import { cn } from '@/utils/cn'
import { AnimatePresence, motion } from 'framer-motion'
import { AlertCircle, CreditCard, Info, Zap } from 'lucide-react'

interface UserLimitsProps {
  className?: string
}

const FALLBACK_LIMITS: UserLimitsType = {
  user_id: 'unknown',
  tier: 'free',
  daily_trial_limit: 100,
  trials_used_today: 0,
  max_parallel_studies: 1,
  last_reset_date: new Date().toISOString(),
}

const numberFormatter = new Intl.NumberFormat('en-US')

export function UserLimits({ className }: UserLimitsProps) {
  const { data: limits, isLoading, isRefetching, error, refetch } = useUserLimits()
  const { formattedRemaining } = useUtcMidnightCountdown()
  const { data: activeStudyData } = useActiveStudyCount()

  if (isLoading) {
    return (
      <UserLimitsSkeleton className={className} />
    )
  }

  const displayLimits = limits ?? FALLBACK_LIMITS
  const dailyLimit = Math.max(0, displayLimits.daily_trial_limit ?? 0)
  const usedToday = Math.max(0, displayLimits.trials_used_today ?? 0)
  const reserved = Math.max(0, displayLimits.credits_reserved ?? 0)
  const creditsAvailable = displayLimits.credits_available
  const remaining = Math.max(
    0,
    typeof creditsAvailable === "number" ? creditsAvailable : dailyLimit - usedToday - reserved
  )
  const tierLabel = displayLimits.tier === 'max' ? 'MAX' : (displayLimits.tier?.toUpperCase() || 'FREE')
  const reservedNote =
    reserved > 0 ? ` ${numberFormatter.format(reserved)} credits currently reserved.` : ""
  
  // Calculate percentage used (inverse for remaining bar, or direct for usage?)
  // Usually "remaining" bars go down. Let's show "Change Left" -> bar fills to show available?
  // No, usage bars usually fill up as you use them.
  // Let's do: Bar represents REMAINING capacity. So if 100/100 remaining, bar is full.
  const percentageRemaining = dailyLimit > 0 ? (remaining / dailyLimit) * 100 : 100

  return (
    <div className={cn("relative group", className)}>
      {/* Glow effect */}
      <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000" />
      
      <GlassCard className="relative p-3 border-white/5 bg-black/40 backdrop-blur-md shadow-xl overflow-hidden">
        {/* Error Overlay */}
        {error && (
             <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-3 text-center">
                 <div className="space-y-1.5">
                     <AlertCircle className="w-6 h-6 text-amber-500 mx-auto" />
                     <p className="text-[10px] text-amber-200">Failed to sync credits</p>
                     <Button size="sm" variant="outline" className="h-6 text-[10px] border-amber-500/50 text-amber-500 hover:bg-amber-500/10" onClick={() => refetch()}>
                         Retry
                     </Button>
                 </div>
             </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5">
              <h3 className="text-[10px] font-semibold text-blue-200/50 uppercase tracking-widest flex items-center gap-1">
                <CreditCard className="w-2.5 h-2.5" />
                Daily Credits
              </h3>
              <div className={cn(
                  "px-1.5 py-px rounded-full text-[9px] font-bold uppercase tracking-wide border shadow-sm",
                  tierLabel === 'MAX'
                     ? "bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/30 text-amber-300"
                     : tierLabel === 'PRO'
                     ? "bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border-blue-500/30 text-blue-300"
                     : "bg-zinc-800/50 border-white/10 text-zinc-400"
              )}>
                 {tierLabel}
              </div>
            </div>
            {/* Main Number - uses smaller font for large numbers */}
            <div className="flex items-baseline gap-1">
                <span className={cn(
                    "font-bold tracking-tight text-white tabular-nums",
                    remaining >= 100000 ? "text-lg" : "text-xl",
                    isRefetching && "opacity-70 transition-opacity"
                )}>
                    {numberFormatter.format(remaining)}
                </span>
                <span className="text-[10px] text-zinc-500 font-medium">
                   / {numberFormatter.format(dailyLimit)}
                </span>
            </div>
          </div>

          <TooltipProvider>
             <Tooltip delayDuration={0}>
               <TooltipTrigger asChild>
                 <button className="text-zinc-600 hover:text-zinc-400 transition-colors mt-0.5">
                   <Info className="w-3 h-3" />
                 </button>
               </TooltipTrigger>
               <TooltipContent side="left" className="text-xs max-w-[200px]">
                  Credits reset daily at midnight UTC. Active studies reserve credits and reduce your available balance.{reservedNote}
               </TooltipContent>
             </Tooltip>
          </TooltipProvider>
        </div>

        {/* Progress Bar */}
        <div className="space-y-1 mb-1.5">
           <Progress 
                value={percentageRemaining} 
                className="h-1 bg-white/5" 
                variant={remaining < (dailyLimit * 0.1) ? "danger" : "brand"} // Red if < 10%
           />
           <div className="flex justify-between text-[9px] text-zinc-600 font-medium">
              <span>0%</span>
              <span>Available</span>
              <span>100%</span>
           </div>
        </div>

        <div className="flex items-center justify-end text-[9px] text-zinc-600 font-medium mb-2">
          Resets in <span className="ml-1 tabular-nums text-zinc-500">{formattedRemaining}</span>
        </div>

        {/* Footer Stats */}
        <div className="grid grid-cols-2 gap-px bg-white/5 rounded-md overflow-hidden border border-white/5">
            <div className="bg-white/[0.02] px-2 py-1.5 hover:bg-white/[0.05] transition-colors group/stat">
                <div className="text-[9px] text-zinc-500 uppercase tracking-wider group-hover/stat:text-zinc-400 transition-colors">Used</div>
                <div className="text-[11px] font-medium text-zinc-300 tabular-nums">
                    {numberFormatter.format(usedToday)}
                </div>
            </div>
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <div className="bg-white/[0.02] px-2 py-1.5 hover:bg-white/[0.05] transition-colors group/stat cursor-help">
                    <div className="text-[9px] text-zinc-500 uppercase tracking-wider group-hover/stat:text-zinc-400 transition-colors">Parallel</div>
                    <div className="flex items-center gap-1 text-[11px] font-medium">
                      <Zap className={cn(
                        "w-2.5 h-2.5 transition-colors",
                        (tierLabel === 'MAX' || tierLabel === 'PRO')
                          ? "text-amber-400 fill-amber-400/20 drop-shadow-[0_0_6px_rgba(251,191,36,0.5)]"
                          : "text-zinc-600"
                      )} />
                      <AnimatePresence mode="wait">
                        <motion.span
                          key={activeStudyData?.count ?? 0}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                          className={cn(
                            "tabular-nums transition-colors inline-block",
                            activeStudyData?.count === 0
                              ? "text-zinc-400"
                              : activeStudyData?.count === (displayLimits.max_parallel_studies ?? 1)
                                ? "text-amber-400"
                                : "text-emerald-400"
                          )}
                        >
                          {activeStudyData?.count ?? 0}
                        </motion.span>
                      </AnimatePresence>
                      <span className="text-zinc-600">/</span>
                      <span className="text-zinc-300">{displayLimits.max_parallel_studies ?? 1}x</span>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  {activeStudyData?.count === 0
                    ? "No studies running"
                    : activeStudyData?.count === 1
                      ? "1 study running"
                      : `${activeStudyData?.count} studies running in parallel`}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
        </div>

      </GlassCard>
    </div>
  )
}

function UserLimitsSkeleton({ className }: { className?: string | undefined }) {
    return (
        <div className={cn("relative", className)}>
             <GlassCard className="p-3 border-white/5 bg-black/40">
                <div className="flex justify-between mb-2">
                    <div className="space-y-1">
                         <div className="h-2 w-20 bg-white/10 rounded animate-pulse" />
                         <div className="h-5 w-16 bg-white/10 rounded animate-pulse" />
                    </div>
                    <div className="h-3 w-3 bg-white/10 rounded animate-pulse" />
                </div>
                <div className="h-1 w-full bg-white/10 rounded-full mb-2 animate-pulse" />
                <div className="grid grid-cols-2 gap-px h-9">
                     <div className="bg-white/5 rounded-l animate-pulse" />
                     <div className="bg-white/5 rounded-r animate-pulse" />
                </div>
             </GlassCard>
        </div>
    )
}
